import {
  __name
} from "../../chunk-7QVYU63E.js";
import { z } from "zod";
const FAILED_PREPARE_CALL_ID_PREFIX = "failed-prepare-";
const WalletApiMethodSchema = z.enum([
  "wallet_prepareCalls",
  "wallet_sendPreparedCalls",
  "wallet_getCallsStatus"
]);
const WalletStatusCode = {
  PENDING: 100,
  PRECONFIRMED: 110,
  PRECONFIRMED_ONCHAIN_FAILURE: 115,
  PRECONFIRMED_PARTIAL_FAILURE: 116,
  CROSS_CHAIN_PENDING: 120,
  CONFIRMED: 200,
  OFFCHAIN_FAILURE: 400,
  CROSS_CHAIN_REFUNDED: 410,
  ONCHAIN_FAILURE: 500,
  PARTIAL_FAILURE: 600
};
const CallIdGroupStatusSchema = z.enum([
  "prepare_failed",
  "send_failed",
  "status_failed",
  "prepared",
  "sent",
  "unknown",
  // Statuses from wallet_getCallsStatus response
  "pending",
  "preconfirmed",
  "cross_chain_pending",
  "confirmed",
  "offchain_failure",
  "cross_chain_refunded",
  "onchain_failure",
  "partial_failure"
]);
const AIErrorTypeSchema = z.enum([
  "validation",
  "sponsorship",
  "session_key",
  "signing",
  "contract",
  "execution"
]);
const AIErrorExplanationSchema = z.object({
  /** Original error message from the Wallet API response */
  message: z.string(),
  /** Type/category of the error */
  type: AIErrorTypeSchema,
  /** AI-generated cause explanation */
  explanation: z.string(),
  /** AI-generated remediation steps */
  remediation: z.string()
});
const TenderlySimulationResultSchema = z.object({
  /** Public URL to the shared Tenderly simulation. */
  url: z.string()
});
const TENDERLY_SUPPORTED_NETWORKS = /* @__PURE__ */ new Set([
  "ETH_MAINNET",
  "ETH_SEPOLIA",
  "MATIC_MAINNET",
  "MATIC_AMOY",
  "ARB_MAINNET",
  "ARB_SEPOLIA",
  "OPT_MAINNET",
  "OPT_SEPOLIA",
  "BASE_MAINNET",
  "BASE_SEPOLIA",
  "AVAX_MAINNET",
  "AVAX_FUJI",
  "BNB_MAINNET",
  "BNB_TESTNET",
  "GNOSIS_MAINNET",
  "LINEA_MAINNET",
  "LINEA_SEPOLIA",
  "SCROLL_MAINNET",
  "SCROLL_SEPOLIA",
  "BLAST_MAINNET",
  "BLAST_SEPOLIA",
  "ZKSYNC_MAINNET",
  "ZKSYNC_SEPOLIA",
  "FANTOM_MAINNET",
  "POLYGONZKEVM_MAINNET"
]);
function isTenderlySupportedNetwork(network) {
  if (!network) return false;
  return TENDERLY_SUPPORTED_NETWORKS.has(network);
}
__name(isTenderlySupportedNetwork, "isTenderlySupportedNetwork");
const WalletCallEventErrorSchema = z.object({
  code: z.number().optional(),
  message: z.string().optional(),
  data: z.unknown().optional(),
  name: z.string().optional()
});
const ResponseFailureTypeSchema = z.enum([
  "offchain_failure",
  "cross_chain_refunded",
  "onchain_failure",
  "partial_failure"
]);
const ResponseFailureSchema = z.object({
  /** Group status derived from the status code (e.g., "onchain_failure"). */
  failureType: ResponseFailureTypeSchema,
  /** Raw status code from the response (e.g., 500). */
  statusCode: z.number(),
  /** Human-readable description of the failure for display in the UI. */
  message: z.string(),
  /** Transaction hash from `receipts[0].transactionHash`, when available. */
  transactionHash: z.string().optional()
});
const WalletCallEventSchema = z.object({
  callId: z.string().optional(),
  method: WalletApiMethodSchema.optional(),
  status: z.enum([
    "error",
    "success"
  ]),
  timestamp: z.string(),
  network: z.string().optional(),
  appId: z.string().optional(),
  appName: z.string().optional(),
  durationMs: z.number().optional(),
  request: z.unknown(),
  response: z.unknown(),
  error: WalletCallEventErrorSchema.optional(),
  responseFailure: ResponseFailureSchema.optional()
});
const CallIdGroupSummarySchema = z.object({
  callId: z.string(),
  appId: z.string().optional(),
  appName: z.string().optional(),
  network: z.string().optional(),
  status: CallIdGroupStatusSchema,
  /** ISO timestamp of the most recent event in the group (last activity). */
  timestamp: z.string(),
  /** ISO timestamp of the earliest event in the group (lifecycle start). */
  startedAt: z.string().optional()
});
const CallIdGroupDetailSchema = CallIdGroupSummarySchema.extend({
  events: z.array(WalletCallEventSchema)
});
export {
  AIErrorExplanationSchema,
  AIErrorTypeSchema,
  CallIdGroupDetailSchema,
  CallIdGroupStatusSchema,
  CallIdGroupSummarySchema,
  FAILED_PREPARE_CALL_ID_PREFIX,
  ResponseFailureSchema,
  ResponseFailureTypeSchema,
  TENDERLY_SUPPORTED_NETWORKS,
  TenderlySimulationResultSchema,
  WalletApiMethodSchema,
  WalletCallEventErrorSchema,
  WalletCallEventSchema,
  WalletStatusCode,
  isTenderlySupportedNetwork
};
//# sourceMappingURL=wallet-api-logs.js.map